import React, { useEffect, useState } from "react";
import { Loader2, Palette, Ruler, Sun, ChevronDown, RotateCcw, Sparkles, ShoppingCart, Check, PartyPopper, Package, Type, Wand2, Share2 } from "lucide-react";
import http from "@/api";
import { useApp } from "@/context";
import { getVid } from "@/lib/vid";

/* ضغط data URL عبر canvas قبل إرساله كمرجع — يسرّع الرفع على شبكات الجوال */
const shrinkDataUrl = (dataUrl, maxW = 768) => new Promise((resolve) => {
  try {
    const img = new Image();
    img.onload = () => {
      try {
        const scale = Math.min(1, maxW / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale), h = Math.round(img.height * scale);
        const c = document.createElement("canvas");
        c.width = w; c.height = h;
        c.getContext("2d").drawImage(img, 0, 0, w, h);
        resolve(c.toDataURL("image/jpeg", 0.82));
      } catch { resolve(dataUrl); }
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  } catch { resolve(dataUrl); }
});

const COLORS = [
  { v: "white", ar: "أبيض", en: "White", sw: "#f8f8f5", p: "pure white like fresh white roses" },
  { v: "cream", ar: "كريمي", en: "Cream", sw: "#f5ead6", p: "natural ivory cream like garden roses" },
  { v: "blush", ar: "وردي بلاش", en: "Blush", sw: "#f7d5dd", p: "soft natural blush pink like fresh garden roses" },
  { v: "pink", ar: "وردي", en: "Pink", sw: "#ef93b4", p: "natural rose pink" },
  { v: "pink_d", ar: "وردي غامق", en: "Dark pink", sw: "#d5478f", p: "deep natural rose pink" },
  { v: "fuchsia", ar: "فوشي", en: "Fuchsia", sw: "#c2185b", p: "natural vivid fuchsia like fresh ranunculus" },
  { v: "red", ar: "أحمر", en: "Red", sw: "#c0392b", p: "classic natural red like fresh red roses" },
  { v: "burgundy", ar: "خمري", en: "Burgundy", sw: "#7b1e2b", p: "deep natural burgundy like dark red roses" },
  { v: "coral", ar: "مرجاني", en: "Coral", sw: "#f88379", p: "natural coral like coral peonies" },
  { v: "peach", ar: "خوخي", en: "Peach", sw: "#fdba8c", p: "soft natural peach like peach roses" },
  { v: "apricot", ar: "مشمشي", en: "Apricot", sw: "#f7b26a", p: "warm natural apricot like apricot roses" },
  { v: "orange", ar: "برتقالي", en: "Orange", sw: "#e8762c", p: "warm natural orange like orange tulips" },
  { v: "yellow_l", ar: "أصفر فاتح", en: "Light yellow", sw: "#fde68a", p: "pale natural butter yellow like soft yellow roses" },
  { v: "yellow", ar: "أصفر", en: "Yellow", sw: "#eab308", p: "sunny natural yellow like fresh yellow roses" },
  { v: "lavender", ar: "لافندر", en: "Lavender", sw: "#b57edc", p: "gentle natural lavender like fresh lavender blooms" },
  { v: "lilac", ar: "ليلكي فاتح", en: "Lilac", sw: "#c9a8e0", p: "light natural lilac like lilac blossoms" },
  { v: "purple", ar: "بنفسجي غامق", en: "Deep purple", sw: "#5b2a86", p: "deep natural violet like purple tulips and lisianthus" },
];
const BALLOON_COLORS = [
  { v: "white", ar: "أبيض", en: "White", sw: "#f8f8f5", p: "pearl white" },
  { v: "red", ar: "أحمر", en: "Red", sw: "#c0392b", p: "glossy red" },
  { v: "pink", ar: "وردي", en: "Pink", sw: "#e79ac0", p: "soft pink" },
  { v: "gold", ar: "ذهبي", en: "Gold", sw: "#d4af37", p: "metallic gold chrome" },
  { v: "silver", ar: "فضي", en: "Silver", sw: "#c0c0c8", p: "metallic silver chrome" },
  { v: "black", ar: "أسود", en: "Black", sw: "#1c1c1c", p: "matte black" },
];
const WRAP_COLORS = [
  { v: "beige", ar: "بيج", en: "Beige", sw: "#e8dcc8", p: "elegant cream beige" },
  { v: "kraft", ar: "كرافت", en: "Kraft", sw: "#c8a165", p: "natural kraft brown" },
  { v: "white", ar: "أبيض", en: "White", sw: "#f8f8f5", p: "pure white" },
  { v: "black", ar: "أسود", en: "Black", sw: "#1c1c1c", p: "matte black" },
  { v: "pink", ar: "وردي", en: "Pink", sw: "#e79ac0", p: "soft pink" },
  { v: "red", ar: "أحمر", en: "Red", sw: "#c0392b", p: "deep red" },
  { v: "gold", ar: "ذهبي", en: "Gold", sw: "#d4af37", p: "luxurious gold" },
  { v: "purple", ar: "بنفسجي", en: "Purple", sw: "#8e44ad", p: "elegant purple" },
  { v: "blue", ar: "أزرق", en: "Blue", sw: "#2563eb", p: "royal blue" },
];
const INKS = [
  { v: "gold", ar: "ذهبي", en: "Gold", sw: "#d4af37", p: "luxurious gold" },
  { v: "silver", ar: "فضي", en: "Silver", sw: "#c0c0c8", p: "shiny silver" },
  { v: "white", ar: "أبيض", en: "White", sw: "#ffffff", p: "elegant white" },
  { v: "black", ar: "أسود", en: "Black", sw: "#1c1c1c", p: "bold black" },
  { v: "pink", ar: "وردي", en: "Pink", sw: "#e79ac0", p: "soft pink" },
  { v: "red", ar: "أحمر", en: "Red", sw: "#c0392b", p: "deep red" },
];
const FONTS = [
  { v: "naskh", ar: "نسخ واضح", en: "Naskh", fam: "Amiri" },
  { v: "thuluth", ar: "ثلث فاخر", en: "Thuluth", fam: "Rakkas" },
  { v: "diwani", ar: "ديواني", en: "Diwani", fam: "Mirza" },
  { v: "ruqaa", ar: "رقعة", en: "Ruq'ah", fam: "Aref Ruqaa" },
  { v: "modern", ar: "عصري بسيط", en: "Modern", fam: "Reem Kufi" },
  { v: "handwritten", ar: "يدوي أنيق", en: "Handwritten", fam: "Gulzar" },
];
const FONT_PREVIEW_CSS =
  "https://fonts.googleapis.com/css2?family=Amiri&family=Aref+Ruqaa&family=Gulzar&family=Mirza&family=Rakkas&family=Reem+Kufi&display=swap";
const LIGHTS = [
  { v: "day", ar: "نهار", en: "Day" },
  { v: "night", ar: "ليل", en: "Night" },
  { v: "warm", ar: "إضاءة صفراء", en: "Warm" },
  { v: "white", ar: "إضاءة بيضاء", en: "White" },
];
const TABS = [
  { k: "color", ar: "اللون", en: "Color", icon: Palette },
  { k: "balloons", ar: "بالونات", en: "Balloons", icon: PartyPopper },
  { k: "box", ar: "التغليف", en: "Wrap", icon: Package },
  { k: "custom", ar: "اطلب تعديلك", en: "Your wish", icon: Wand2 },
  { k: "size_compare", ar: "الأحجام", en: "Sizes", icon: Ruler },
  { k: "lighting", ar: "الإضاءة", en: "Lighting", icon: Sun },
];

export default function ProductAIPreview({ productId, baseImage, lang, product }) {
  const { addToCart } = useApp();
  const ar = lang === "ar";
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("color");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [active, setActive] = useState("");
  const [err, setErr] = useState("");
  // Multi-select colours (array) — toggle chips, then generate the mix.
  const [sel, setSel] = useState([]);
  const [resultLabel, setResultLabel] = useState(""); // وصف نتيجة التعديل الحالي (للسلة)
  const [boxText, setBoxText] = useState("");
  const [inkColor, setInkColor] = useState("gold");
  const [fontStyle, setFontStyle] = useState("naskh");
  const [textSize, setTextSize] = useState("medium");
  const [wrapSel, setWrapSel] = useState([]);
  const [wrapTarget, setWrapTarget] = useState("paper");
  const [sizePrices, setSizePrices] = useState(null);
  const [selectedSize, setSelectedSize] = useState("");
  const [sizeAdded, setSizeAdded] = useState(false);
  const [sizeImages, setSizeImages] = useState({});
  const [sizeLoading, setSizeLoading] = useState(false);
  const [sizeAdding, setSizeAdding] = useState(false);
  const [customText, setCustomText] = useState("");
  const [customVerdict, setCustomVerdict] = useState(null);
  const [stemsN, setStemsN] = useState("");
  const [stemsPick, setStemsPick] = useState(null);
  const [stemsImgs, setStemsImgs] = useState({});
  const [stemsAdding, setStemsAdding] = useState(false);
  const [stemsAdded, setStemsAdded] = useState(false);
  const [fees, setFees] = useState({});
  const [resultAddon, setResultAddon] = useState("");
  const [balloons, setBalloons] = useState({});
  const [resultBalloons, setResultBalloons] = useState(null);
  useEffect(() => {
    if (open && !Object.keys(fees).length) {
      http.get("/ai/addon-fees").then(({ data }) => setFees(data || {})).catch(() => {});
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (tab === "box" && !document.getElementById("rg-font-previews")) {
      const l = document.createElement("link");
      l.id = "rg-font-previews"; l.rel = "stylesheet"; l.href = FONT_PREVIEW_CSS;
      document.head.appendChild(l);
    }
    if (tab === "size_compare" && !sizePrices) {
      http.get(`/ai/size-prices/${productId}`, { timeout: 45000 })
        .then(({ data }) => setSizePrices(data))
        .catch(() => setSizePrices({ error: true }));
    }
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps
  const [adding, setAdding] = useState(false);
  const [addedOk, setAddedOk] = useState(false);

  const toggleColor = (v) => {
    setSel((prev) => prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]);
  };

  const colorLabel = (vals) =>
    vals.map((v) => {
      const c = COLORS.find((x) => x.v === v);
      return c ? (ar ? c.ar : c.en) : v;
    }).join(" + ");

  const run = async (edit_type, value, label, extra = {}) => {
    setLoading(true); setErr(""); setActive(`${edit_type}:${value || ""}`);
    setAddedOk(false);
    // التعديلات تتراكم: نبني فوق آخر نتيجة معروضة (إلا مقارنة الأحجام)
    const stackBase = result && resultAddon !== "size_compare" && edit_type !== "size_compare" ? result : null;
    try {
      // ضغط الصورة المرجعية قبل الإرسال — يخفض الرفع من ~600KB إلى ~80KB (أسرع بكثير على الجوال)
      const base = stackBase ? await shrinkDataUrl(stackBase) : null;
      const { data } = await http.post("/ai/product-edit",
        { product_id: productId, edit_type, value, language: lang,
          ...(base ? { base_image: base } : {}), ...extra }, { timeout: 120000 });
      setResult(data.image);
      const combined = stackBase && resultLabel && label ? `${resultLabel} + ${label}` : (label || resultLabel || "");
      setResultLabel(edit_type === "size_compare" || edit_type === "lighting" ? "" : combined.slice(0, 90));
      setResultAddon(edit_type);
    } catch {
      setErr(ar ? "تعذّر التوليد — حاول مجدداً" : "Couldn't generate — try again");
    } finally { setLoading(false); }
  };

  const generateColors = () => {
    if (!sel.length) return;
    const phrases = sel.map((v) => COLORS.find((x) => x.v === v)?.p || v);
    const value = phrases.length === 1
      ? phrases[0]
      : `a beautiful harmonious mix of ${phrases.join(" and ")}`;
    run("color", value, (ar ? "لون: " : "Colour: ") + colorLabel(sel));
  };

  const setBalloonQty = (v, d) =>
    setBalloons((prev) => {
      const n = Math.max(0, (prev[v] || 0) + d);
      const next = { ...prev, [v]: n };
      if (!n) delete next[v];
      return next;
    });
  const balloonCount = Object.values(balloons).reduce((s, n) => s + n, 0);
  const balloonUnit = Number(fees.balloon_unit ?? 10);
  const balloonLabel = (obj) =>
    Object.entries(obj || {}).map(([v, n]) => {
      const c = BALLOON_COLORS.find((x) => x.v === v);
      return `${n} ${c ? (ar ? c.ar : c.en) : v}`;
    }).join(" + ");

  const generateBalloons = () => {
    if (!balloonCount) return;
    const snapshot = { ...balloons };
    const phrase = Object.entries(snapshot)
      .map(([v, n]) => `${n} ${BALLOON_COLORS.find((x) => x.v === v)?.p || v}`)
      .join(", ");
    setResultBalloons(snapshot);
    run("add_balloons", phrase, (ar ? "بالونات: " : "Balloons: ") + balloonLabel(snapshot));
  };

  // Customer wants THIS customisation delivered → persist the image, add to cart.
  const resultBalloonCount = resultAddon === "add_balloons"
    ? Object.values(resultBalloons || {}).reduce((s, n) => s + n, 0) : 0;
  const addonFee = resultAddon === "add_balloons"
    ? resultBalloonCount * balloonUnit
    : Number(fees[resultAddon] || 0);
  const addCustomToCart = async () => {
    if (!result || !product || adding || !resultLabel) return;
    setAdding(true); setErr("");
    try {
      const { data } = await http.post("/ai/preview-save", { image: result }, { timeout: 60000 });
      addToCart(product, 1, {
        id: `ai-custom-${active.replace(/[^a-z_]/gi, "").slice(0, 24)}-${Date.now() % 100000}`,
        name: resultLabel + (addonFee > 0 ? ` (+${addonFee} ${ar ? "ر.ق" : "QAR"})` : ""),
        price_sar: (Number(product.price_sar) || 0) + addonFee,
        image: data.url,
        custom_color: {
          label: resultLabel, image: data.url, addon: resultAddon,
          ...(resultAddon === "add_balloons" && resultBalloons
            ? { balloons: resultBalloons, balloon_count: resultBalloonCount } : {}),
        },
      });
      setAddedOk(true);
      setTimeout(() => setAddedOk(false), 6000);
    } catch {
      setErr(ar ? "تعذّر الحفظ — حاول مجدداً" : "Couldn't save — try again");
    }
    setAdding(false);
  };

  const SIZE_LBL = { small: ar ? "صغير" : "Small", medium: ar ? "متوسط" : "Medium", large: ar ? "كبير" : "Large" };
  const pickSize = async (k) => {
    const off = selectedSize === k;
    setSelectedSize(off ? "" : k); setSizeAdded(false); setStemsPick(null);
    if (off || k === sizePrices?.base || sizeImages[k]) return;
    setSizeLoading(true); setErr("");
    try {
      const { data } = await http.post("/ai/size-image", { product_id: productId, size: k }, { timeout: 90000 });
      if (data.image) setSizeImages((prev) => ({ ...prev, [k]: data.image }));
    } catch {
      setErr(ar ? "تعذّر توليد صورة الحجم — السعر صحيح ويمكنك الإضافة للسلة" : "Couldn't generate the size photo — the price is correct, you can still add to cart");
    }
    setSizeLoading(false);
  };

  const addSizeToCart = async () => {
    if (!selectedSize || sizeAdding) return;
    setSizeAdding(true);
    const L = SIZE_LBL[selectedSize];
    let img = product?.image;
    let extra = {};
    const gen = sizeImages[selectedSize];
    if (gen) {
      try {
        const { data } = await http.post("/ai/preview-save", { image: gen }, { timeout: 60000 });
        img = data.url;
        extra = { custom_color: { label: ar ? `حجم ${L}` : `${L} size`, image: data.url, addon: "size" } };
      } catch {}
    }
    addToCart(product, 1, {
      id: `size-${selectedSize}`,
      name: ar ? `حجم ${L}` : `${L} size`,
      price_sar: Number(sizePrices.prices[selectedSize]),
      image: img,
      ...extra,
    });
    setSizeAdding(false); setSizeAdded(true);
    setTimeout(() => setSizeAdded(false), 5000);
  };

  const stemsPriceOf = (n) => {
    const cs = sizePrices?.custom_stems;
    if (!cs || !n) return null;
    return Math.max(5, Math.floor((cs.unit * n) / 5 + 0.5) * 5);
  };

  const genStems = async () => {
    const n = parseInt(stemsN, 10);
    if (!n || n < 3 || n > 100) { setErr(ar ? "عدد الورود من 3 إلى 100" : "Stem count must be 3-100"); return; }
    setSelectedSize(""); setSizeAdded(false); setStemsAdded(false); setErr("");
    setStemsPick(n);
    if (stemsImgs[n]) return;
    setSizeLoading(true);
    try {
      const { data } = await http.post("/ai/size-image", { product_id: productId, size: "custom", stems: n }, { timeout: 90000 });
      if (data.image) setStemsImgs((prev) => ({ ...prev, [n]: data.image }));
      else setStemsPick(null);
    } catch {
      setErr(ar ? "تعذّر توليد الصورة — حاول مجدداً" : "Couldn't generate — try again");
      setStemsPick(null);
    }
    setSizeLoading(false);
  };

  const addStemsToCart = async () => {
    if (!stemsPick || stemsAdding) return;
    setStemsAdding(true);
    const price = stemsPriceOf(stemsPick);
    let img = product?.image;
    let extra = {};
    const gen = stemsImgs[stemsPick];
    if (gen) {
      try {
        const { data } = await http.post("/ai/preview-save", { image: gen }, { timeout: 60000 });
        img = data.url;
        extra = { custom_color: { label: ar ? `${stemsPick} وردة` : `${stemsPick} stems`, image: data.url, addon: "size" } };
      } catch {}
    }
    addToCart(product, 1, {
      id: `stems-${stemsPick}`,
      name: ar ? `${stemsPick} وردة` : `${stemsPick} stems`,
      price_sar: price,
      image: img,
      ...extra,
    });
    setStemsAdding(false); setStemsAdded(true);
    setTimeout(() => setStemsAdded(false), 5000);
  };

  const runCustom = async (txt) => {
    const t = (txt ?? customText).trim();
    if (t.length < 4 || loading) return;
    setLoading(true); setErr(""); setCustomVerdict(null); setAddedOk(false);
    try {
      const { data } = await http.post("/ai/custom-edit",
        { product_id: productId, text: t, language: lang }, { timeout: 120000 });
      if (data.feasible) {
        setResult(data.image);
        setActive("custom_edit:req");
        setResultLabel((ar ? "طلب خاص: " : "Request: ") + t.slice(0, 70));
        setResultAddon("custom_edit");
      } else {
        setCustomVerdict(data);
      }
    } catch {
      setErr(ar ? "تعذّر تنفيذ الطلب — حاول مجدداً" : "Couldn't process the request — try again");
    }
    setLoading(false);
  };

  const [sharing, setSharing] = useState(false);
  const shareDesign = async () => {
    if (!result || !resultLabel || sharing) return;
    setSharing(true); setErr("");
    try {
      const { data } = await http.post("/ai/share-design",
        { product_id: productId, image: result, label: resultLabel, vid: getVid() }, { timeout: 60000 });
      const url = `${window.location.origin}/api/share/design/${data.id}`;
      const text = ar
        ? `شوفوا التصميم اللي سوّيته 😍🌸 ${resultLabel}`
        : `Check out my custom design 😍🌸 ${resultLabel}`;
      if (navigator.share) {
        try { await navigator.share({ title: "RG", text, url }); } catch {}
      } else {
        window.open(`https://wa.me/?text=${encodeURIComponent(`${text}\n${url}`)}`, "_blank", "noopener");
      }
    } catch {
      setErr(ar ? "تعذّرت المشاركة — حاول مجدداً" : "Couldn't share — try again");
    }
    setSharing(false);
  };

  const sizeShown = tab === "size_compare"
    ? ((stemsPick && stemsImgs[stemsPick]) || (selectedSize ? (sizeImages[selectedSize] || baseImage) : null))
    : null;
  const sizeBadge = stemsPick
    ? (ar ? `${stemsPick} وردة` : `${stemsPick} stems`)
    : selectedSize
      ? (ar ? `حجم ${SIZE_LBL[selectedSize]}` : `${SIZE_LBL[selectedSize]} size`)
        + (selectedSize === sizePrices?.base ? (ar ? " (الصورة الأصلية)" : " (original)") : "")
      : "";

  return (
    <div className="rounded-2xl border overflow-hidden" style={{ borderColor: "var(--border)" }} data-testid="product-ai-preview">
      <button onClick={() => setOpen((o) => !o)}
              className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold hover:bg-black/[0.02]"
              data-testid="ai-preview-toggle">
        <span className="flex items-center gap-2"><Palette size={18} className="text-emerald-700" />
          {ar ? "صمّم هديتك بنفسك — لون، بالونات، تغليف ✨" : "Design your gift — colours, balloons & wrap ✨"}
        </span>
        <ChevronDown size={18} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="px-4 pb-4 pt-1 space-y-3" data-testid="ai-preview-body">
          <div className="text-[11px] rounded-xl px-3 py-2 bg-emerald-50 text-emerald-900 font-semibold leading-relaxed"
               data-testid="ai-preview-howto">
            {ar
              ? "✨ بسيطة جداً: ١) اختر التعديل من الأزرار 👇 ٢) شاهد صورته الحقيقية ٣) أعجبك؟ أضفه للسلة ويصلك بنفس الشكل"
              : "✨ Super simple: 1) pick an option below 2) see a real photo of it 3) love it? Add to cart and get it exactly like that"}
          </div>
          <div className="flex gap-1 overflow-x-auto pb-0.5">
            {TABS.filter((t) => t.k !== "size_compare" || product?.sizes_enabled).map((t) => (
              <button key={t.k} onClick={() => setTab(t.k)}
                      className={`shrink-0 flex items-center justify-center gap-1 py-2 px-2.5 rounded-xl text-xs font-medium transition-all ${tab === t.k ? "bg-emerald-50 text-emerald-800 font-semibold" : "bg-black/[0.05] hover:bg-black/10"}`}
                      data-testid={`ai-preview-tab-${t.k}`}>
                <t.icon size={14} /> {ar ? t.ar : t.en}
              </button>
            ))}
          </div>

          {tab === "color" && (
            <div className="space-y-2">
              <div className="text-[11px] opacity-60">
                {ar ? "اختر لوناً أو أكثر لدمجها معاً ثم اضغط «جرّب»" : "Pick one or more colours to mix, then tap Generate"}
              </div>
              <div className="flex flex-wrap gap-2" data-testid="ai-preview-colors">
                {COLORS.map((c) => {
                  const on = sel.includes(c.v);
                  return (
                    <button key={c.v} onClick={() => toggleColor(c.v)} disabled={loading}
                            aria-pressed={on}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs transition-all ${on ? "border-emerald-700 bg-emerald-50 font-semibold" : "border-[var(--border)]"}`}
                            data-testid={`ai-preview-color-${c.v}`}>
                      <span className="w-4 h-4 rounded-full border flex items-center justify-center" style={{ background: c.sw, borderColor: "rgba(0,0,0,0.15)" }}>
                        {on && <Check size={10} strokeWidth={3.5} className="text-white" style={{ filter: "drop-shadow(0 0 1px rgba(0,0,0,0.7))" }} />}
                      </span>
                      {ar ? c.ar : c.en}
                    </button>
                  );
                })}
              </div>
              <button onClick={generateColors} disabled={loading || !sel.length}
                      className="btn-primary w-full !py-2.5 text-sm disabled:opacity-40"
                      data-testid="ai-preview-generate">
                {loading ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
                {sel.length > 1
                  ? (ar ? `جرّب مزيج: ${colorLabel(sel)}` : `Try mix: ${colorLabel(sel)}`)
                  : (ar ? "جرّب هذا اللون" : "Try this colour")}
              </button>
            </div>
          )}

          {tab === "balloons" && (
            <div className="space-y-2">
              <div className="text-[11px] opacity-60">
                {ar
                  ? `🎈 اختر عدد البالونات من كل لون — سعر البالون ${balloonUnit} ر.ق`
                  : `🎈 Pick how many balloons of each colour — ${balloonUnit} QAR per balloon`}
              </div>
              <div className="grid grid-cols-2 gap-2" data-testid="ai-preview-balloons">
                {BALLOON_COLORS.map((b) => {
                  const n = balloons[b.v] || 0;
                  return (
                    <div key={b.v}
                         className={`flex items-center justify-between gap-0.5 ps-1.5 pe-1 py-1.5 rounded-xl border text-[11px] transition-all ${n ? "border-emerald-700 bg-emerald-50" : "border-[var(--border)]"}`}
                         data-testid={`ai-preview-balloon-${b.v}`}>
                      <span className="flex items-center gap-1 min-w-0">
                        <span className="w-3.5 h-3.5 rounded-full border shrink-0" style={{ background: b.sw, borderColor: "rgba(0,0,0,0.15)" }} />
                        <span className="truncate">{ar ? b.ar : b.en}</span>
                      </span>
                      <span className="flex items-center gap-0.5 shrink-0">
                        <button onClick={() => setBalloonQty(b.v, -1)} disabled={loading || !n}
                                className="w-5 h-5 rounded-full bg-black/[0.06] font-bold disabled:opacity-30"
                                data-testid={`ai-preview-balloon-${b.v}-minus`}>−</button>
                        <span className={`w-4 text-center font-semibold ${n ? "text-emerald-800" : "opacity-40"}`}
                              data-testid={`ai-preview-balloon-${b.v}-count`}>{n}</span>
                        <button onClick={() => setBalloonQty(b.v, 1)} disabled={loading}
                                className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-800 font-bold"
                                data-testid={`ai-preview-balloon-${b.v}-plus`}>+</button>
                      </span>
                    </div>
                  );
                })}
              </div>
              {balloonCount > 0 && (
                <div className="flex items-center justify-between text-xs font-semibold px-1" data-testid="ai-preview-balloon-total">
                  <span>{balloonCount} {ar ? "بالون" : "balloons"} ({balloonLabel(balloons)})</span>
                  <span className="text-emerald-800">{balloonCount * balloonUnit} {ar ? "ر.ق" : "QAR"}</span>
                </div>
              )}
              <button onClick={generateBalloons} disabled={loading || !balloonCount}
                      className="btn-primary w-full !py-2.5 text-sm disabled:opacity-40"
                      data-testid="ai-preview-balloon-generate">
                {loading ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
                {balloonCount
                  ? (ar ? `جرّب ${balloonCount} بالون — ${balloonCount * balloonUnit} ر.ق` : `Try ${balloonCount} balloons — ${balloonCount * balloonUnit} QAR`)
                  : (ar ? "اختر البالونات أولاً" : "Pick balloons first")}
              </button>
            </div>
          )}

          {tab === "box" && (
            <div className="space-y-3">
              <div>
                <div className="flex items-center gap-1.5 mb-1.5" data-testid="ai-preview-wrap-target">
                  <span className="text-[11px] opacity-60">{ar ? "🎀 غيّر لون:" : "🎀 Recolor:"}</span>
                  {[["paper", ar ? "ورق التغليف" : "Wrapping paper"], ["box", ar ? "البوكس" : "The box"]].map(([k, l]) => (
                    <button key={k} onClick={() => setWrapTarget(k)} disabled={loading} aria-pressed={wrapTarget === k}
                            className={`px-2.5 py-1 rounded-full border text-[11px] transition-all ${wrapTarget === k ? "border-emerald-700 bg-emerald-50 font-bold" : "border-[var(--border)]"}`}
                            data-testid={`ai-preview-wrap-target-${k}`}>{l}</button>
                  ))}
                </div>
                <div className="text-[11px] opacity-60 mb-1.5">
                  {ar ? "اختر لوناً أو أكثر لدمجها معاً (الورد يبقى كما هو)" : "Pick one or more colours to mix (flowers stay the same)"}
                </div>
                <div className="flex flex-wrap gap-2" data-testid="ai-preview-wrap-colors">
                  {WRAP_COLORS.map((c) => {
                    const on = wrapSel.includes(c.v);
                    return (
                      <button key={c.v}
                              onClick={() => setWrapSel((prev) => prev.includes(c.v) ? prev.filter((x) => x !== c.v) : [...prev, c.v])}
                              disabled={loading} aria-pressed={on}
                              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs transition-all ${on ? "border-emerald-700 bg-emerald-50 font-semibold" : "border-[var(--border)]"}`}
                              data-testid={`ai-preview-wrap-color-${c.v}`}>
                        <span className="w-4 h-4 rounded-full border flex items-center justify-center" style={{ background: c.sw, borderColor: "rgba(0,0,0,0.15)" }}>
                          {on && <Check size={10} strokeWidth={3.5} className="text-white" style={{ filter: "drop-shadow(0 0 1px rgba(0,0,0,0.7))" }} />}
                        </span>
                        {ar ? c.ar : c.en}
                      </button>
                    );
                  })}
                </div>
                <button onClick={() => {
                          if (!wrapSel.length) return;
                          const picked = wrapSel.map((v) => WRAP_COLORS.find((x) => x.v === v)).filter(Boolean);
                          const value = picked.length === 1
                            ? picked[0].p
                            : `a beautiful harmonious mix of ${picked.map((c) => c.p).join(" and ")}`;
                          const tLabel = wrapTarget === "box" ? (ar ? "بوكس " : "Box ") : (ar ? "تغليف " : "Wrap ");
                          const label = tLabel + picked.map((c) => (ar ? c.ar : c.en)).join(" + ");
                          run(wrapTarget === "box" ? "box_color" : "wrap_color", value, label);
                        }}
                        disabled={loading || !wrapSel.length}
                        className="btn-primary w-full !py-2.5 text-sm disabled:opacity-40 mt-2"
                        data-testid="ai-preview-wrap-generate">
                  {loading ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
                  {wrapSel.length
                    ? (ar ? `جرّب ${wrapTarget === "box" ? "لون البوكس" : "التغليف"}: ${wrapSel.map((v) => { const c = WRAP_COLORS.find((x) => x.v === v); return c ? c.ar : v; }).join(" + ")}` : `Try ${wrapTarget}`)
                    : (ar ? "اختر اللون أولاً" : "Pick a colour first")}
                </button>
              </div>
              <div>
                <div className="text-[11px] opacity-60 mb-1.5 flex items-center gap-1">
                  <Type size={12} /> {ar ? "✍️ اكتب اسماً أو عبارة على البوكس" : "✍️ Write a name/phrase on the box"}
                </div>
                <div className="flex flex-wrap gap-1.5 mb-2" data-testid="ai-preview-ink-colors">
                  <span className="text-[11px] opacity-60 self-center">{ar ? "لون الكتابة:" : "Ink:"}</span>
                  {INKS.map((k) => (
                    <button key={k.v} onClick={() => setInkColor(k.v)} disabled={loading}
                            aria-pressed={inkColor === k.v}
                            className={`flex items-center gap-1 px-2 py-1 rounded-full border text-[11px] transition-all ${inkColor === k.v ? "border-emerald-700 bg-emerald-50 font-semibold" : "border-[var(--border)]"}`}
                            data-testid={`ai-preview-ink-${k.v}`}>
                      <span className="w-3.5 h-3.5 rounded-full border" style={{ background: k.sw, borderColor: "rgba(0,0,0,0.2)" }} />
                      {ar ? k.ar : k.en}
                    </button>
                  ))}
                </div>
                <div className="mb-2" data-testid="ai-preview-font-styles">
                  <span className="text-[11px] opacity-60 block mb-1">{ar ? "نوع الخط — اضغط لمعاينة الشكل:" : "Font — tap to preview:"}</span>
                  <div className="flex flex-wrap gap-1.5">
                    {FONTS.map((f) => (
                      <button key={f.v} onClick={() => setFontStyle(f.v)} disabled={loading}
                              aria-pressed={fontStyle === f.v}
                              className={`px-2.5 py-1 rounded-xl border text-center transition-all ${fontStyle === f.v ? "border-emerald-700 bg-emerald-50" : "border-[var(--border)]"}`}
                              data-testid={`ai-preview-font-${f.v}`}>
                        <span className="block leading-snug" style={{ fontFamily: `'${f.fam}', serif`, fontSize: 17 }}>
                          {(boxText.trim() || (ar ? "أم خليفة" : "Sample")).slice(0, 12)}
                        </span>
                        <span className={`block text-[9px] ${fontStyle === f.v ? "text-emerald-700 font-bold" : "opacity-50"}`}>{ar ? f.ar : f.en}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5 mb-2" data-testid="ai-preview-text-sizes">
                  <span className="text-[11px] opacity-60 self-center">{ar ? "حجم الكتابة:" : "Text size:"}</span>
                  {[["small", ar ? "صغير" : "Small"], ["medium", ar ? "وسط" : "Medium"], ["large", ar ? "كبير" : "Large"]].map(([k, l]) => (
                    <button key={k} onClick={() => setTextSize(k)} disabled={loading} aria-pressed={textSize === k}
                            className={`px-2.5 py-1 rounded-full border text-[11px] transition-all ${textSize === k ? "border-emerald-700 bg-emerald-50 font-bold" : "border-[var(--border)]"}`}
                            data-testid={`ai-preview-text-size-${k}`}>{l}</button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input value={boxText} onChange={(e) => setBoxText(e.target.value)} maxLength={40}
                         disabled={loading}
                         placeholder={ar ? "مثال: أم خليفة 🤍" : "e.g. Umm Khalifa 🤍"}
                         className="input-field flex-1 !py-2 !text-sm"
                         data-testid="ai-preview-box-text-input" />
                  <button onClick={() => {
                            if (!boxText.trim()) return;
                            const k = INKS.find((x) => x.v === inkColor) || INKS[0];
                            const f = FONTS.find((x) => x.v === fontStyle) || FONTS[0];
                            run("box_text", boxText.trim(),
                                (ar ? `كتابة ${k.ar} بخط ${f.ar}: ` : `Box text (${k.en}, ${f.en}): `) + boxText.trim(),
                                { ink: k.p, font: f.v, text_size: textSize });
                          }}
                          disabled={loading || !boxText.trim()}
                          className="btn-primary !py-2 !px-4 text-sm disabled:opacity-40"
                          data-testid="ai-preview-box-text-btn">
                    {loading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                  </button>
                </div>
              </div>
            </div>
          )}

          {tab === "custom" && (
            <div className="space-y-2" data-testid="ai-preview-custom-tab">
              <div className="text-[11px] opacity-60">
                {ar
                  ? "✍️ اكتب بالضبط ماذا تريد في هذا المنتج أو أي تعديل عليه — وسنريك صورته الحقيقية"
                  : "✍️ Describe exactly what you want changed — we'll show you a real photo of it"}
              </div>
              <textarea value={customText} onChange={(e) => setCustomText(e.target.value)} maxLength={300} rows={3}
                        disabled={loading}
                        placeholder={ar
                          ? "مثال: أبي نص الورد أبيض ونصه أحمر مع شريطة ذهبية ودبدوب صغير بالنص"
                          : "e.g. Half white, half red roses with a gold ribbon and a small teddy in the middle"}
                        className="input-field w-full !py-2 !text-sm"
                        data-testid="ai-preview-custom-input" />
              {Number(fees.custom_edit) > 0 && (
                <div className="text-[11px] font-bold text-amber-700" data-testid="ai-preview-custom-fee-hint">
                  💳 {ar
                    ? `رسم التعديل الخاص: +${Number(fees.custom_edit)} ر.ق يُضاف على سعر المنتج عند الإضافة للسلة`
                    : `Custom edit fee: +${Number(fees.custom_edit)} QAR added on top of the product price`}
                </div>
              )}
              <button onClick={() => runCustom()} disabled={loading || customText.trim().length < 4}
                      className="btn-primary w-full !py-2.5 text-sm disabled:opacity-40"
                      data-testid="ai-preview-custom-generate">
                {loading ? <Loader2 size={15} className="animate-spin" /> : <Wand2 size={15} />}
                {ar ? "نفّذ طلبي وأرني الصورة" : "Do it & show me"}
              </button>
              {customVerdict && !customVerdict.feasible && (
                <div className="rounded-xl p-3 space-y-2 border" style={{ background: "#fffbeb", borderColor: "#fde68a" }}
                     data-testid="ai-preview-custom-verdict">
                  <div className="text-xs font-black text-amber-800">
                    {ar ? "🙏 عذراً — هذا الطلب غير قابل للتنفيذ الواقعي" : "🙏 Sorry — this request isn't realistically possible"}
                  </div>
                  <div className="text-[11px] text-amber-800" data-testid="ai-preview-custom-reason">{customVerdict.message}</div>
                  {customVerdict.suggestions?.length > 0 && (
                    <>
                      <div className="text-[11px] font-bold text-amber-900">{ar ? "💡 جرّب بدلاً منه — اضغط أي اقتراح:" : "💡 Try instead — tap a suggestion:"}</div>
                      <div className="flex flex-wrap gap-1.5">
                        {customVerdict.suggestions.map((s, i) => (
                          <button key={i} onClick={() => { setCustomText(s); runCustom(s); }} disabled={loading}
                                  className="chip !text-[11px] !bg-white"
                                  data-testid={`ai-preview-custom-sugg-${i}`}>{s}</button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {tab === "lighting" && (
            <div className="flex flex-wrap gap-2" data-testid="ai-preview-lights">
              {LIGHTS.map((l) => (
                <button key={l.v} onClick={() => run("lighting", l.v, "")} disabled={loading}
                        className={`chip ${active === `lighting:${l.v}` ? "chip-primary" : ""}`}
                        data-testid={`ai-preview-light-${l.v}`}>{ar ? l.ar : l.en}</button>
              ))}
            </div>
          )}
          {tab === "size_compare" && (
            <div className="space-y-2">
              {sizePrices?.prices ? (
                <>
                  <div className="text-[11px] opacity-60">
                    {ar ? "💰 اضغط على أي حجم لتشاهد صورته الحقيقية وسعره" : "💰 Tap any size to see its real photo & price"}
                  </div>
                  <div className="grid grid-cols-3 gap-2" data-testid="ai-preview-size-cards">
                    {["small", "medium", "large"].map((k) => {
                      const isBase = sizePrices.base === k;
                      const on = selectedSize === k;
                      const L = SIZE_LBL[k];
                      return (
                        <button key={k} onClick={() => pickSize(k)} disabled={sizeLoading}
                                aria-pressed={on}
                                className={`relative rounded-xl border-2 p-2 text-center transition-all ${on ? "border-emerald-700 bg-emerald-50" : isBase ? "border-amber-400 bg-amber-50/50" : "border-[var(--border)]"}`}
                                data-testid={`ai-preview-size-card-${k}`}>
                          {isBase && (
                            <span className="absolute -top-2 start-1/2 -translate-x-1/2 rtl:translate-x-1/2 text-[8px] font-black px-1.5 py-0.5 rounded-full bg-amber-400 text-amber-950 whitespace-nowrap">
                              ⭐ {ar ? "الحجم الأساسي بالصورة" : "Base (as photo)"}
                            </span>
                          )}
                          <div className="text-xs font-black mt-1">{L}</div>
                          <div className="text-[9px] opacity-50">{sizePrices.hints?.[k]} {ar ? "وردة" : "stems"}</div>
                          <div className={`text-sm font-black tabular-nums mt-0.5 ${on ? "text-emerald-700" : ""}`}>
                            {Number(sizePrices.prices[k]).toLocaleString("en")} {ar ? "ر.ق" : "QAR"}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  {selectedSize && (
                    <button onClick={addSizeToCart}
                            disabled={sizeAdded || sizeAdding || sizeLoading}
                            className="btn-primary w-full !py-2.5 text-sm"
                            data-testid="ai-preview-size-add">
                      {sizeAdding ? <Loader2 size={15} className="animate-spin" /> : sizeAdded ? <Check size={15} /> : <ShoppingCart size={15} />}
                      {sizeAdded
                        ? (ar ? "تمت الإضافة ✓" : "Added ✓")
                        : (ar
                          ? `أضف للسلة — حجم ${SIZE_LBL[selectedSize]} (${Number(sizePrices.prices[selectedSize]).toLocaleString("en")} ر.ق)`
                          : `Add to cart — ${selectedSize} (${sizePrices.prices[selectedSize]} QAR)`)}
                    </button>
                  )}
                  {sizePrices.custom_stems && (
                    <div className="rounded-xl border p-2.5 space-y-2" style={{ borderColor: "var(--border)" }}
                         data-testid="ai-preview-custom-stems">
                      <div className="text-[11px] font-bold">
                        🌹 {ar ? "حجم مخصص — اكتب عدد الورود اللي تبيه بالضبط" : "Custom size — type the exact stem count"}
                      </div>
                      <div className="flex items-center gap-2">
                        <input type="number" min={3} max={100} value={stemsN}
                               onChange={(e) => { setStemsN(e.target.value); setStemsAdded(false); }}
                               disabled={sizeLoading}
                               placeholder="25"
                               className="input-field !w-24 !py-2 !text-sm text-center"
                               data-testid="ai-preview-stems-input" />
                        <span className="text-xs opacity-60">{ar ? "وردة (3-100)" : "stems (3-100)"}</span>
                        {stemsPriceOf(parseInt(stemsN, 10)) != null && (
                          <span className="ms-auto text-sm font-black text-emerald-800 tabular-nums"
                                data-testid="ai-preview-stems-price">
                            {stemsPriceOf(parseInt(stemsN, 10)).toLocaleString("en")} {ar ? "ر.ق" : "QAR"}
                          </span>
                        )}
                      </div>
                      <button onClick={genStems} disabled={sizeLoading || !stemsN}
                              className="btn-primary w-full !py-2 text-sm disabled:opacity-40"
                              data-testid="ai-preview-stems-generate">
                        {sizeLoading && stemsPick ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                        {stemsN
                          ? (ar ? `شاهد صورة ${stemsN} وردة` : `See ${stemsN} stems photo`)
                          : (ar ? "اكتب العدد أولاً" : "Type the count first")}
                      </button>
                      {stemsPick && stemsImgs[stemsPick] && !sizeLoading && (
                        <button onClick={addStemsToCart} disabled={stemsAdding || stemsAdded}
                                className="btn-primary w-full !py-2 text-sm"
                                data-testid="ai-preview-stems-add">
                          {stemsAdding ? <Loader2 size={14} className="animate-spin" /> : stemsAdded ? <Check size={14} /> : <ShoppingCart size={14} />}
                          {stemsAdded
                            ? (ar ? "تمت الإضافة ✓" : "Added ✓")
                            : (ar
                              ? `أضف للسلة — ${stemsPick} وردة (${stemsPriceOf(stemsPick).toLocaleString("en")} ر.ق)`
                              : `Add to cart — ${stemsPick} stems (${stemsPriceOf(stemsPick)} QAR)`)}
                        </button>
                      )}
                    </div>
                  )}
                </>
              ) : sizePrices?.error ? null : (
                <div className="flex items-center gap-2 text-[11px] opacity-50 py-1" data-testid="ai-preview-size-loading">
                  <Loader2 size={12} className="animate-spin" /> {ar ? "جارٍ قراءة الحجم من الصورة وتسعير الأحجام..." : "Reading size from photo & pricing..."}
                </div>
              )}
              <button onClick={() => run("size_compare", "", "")} disabled={loading}
                      className="btn-outline w-full !py-2.5 text-sm" data-testid="ai-preview-size-btn">
                <Ruler size={15} /> {ar ? "شاهد مقارنة الأحجام بالصور (صغير/متوسط/كبير)" : "See size comparison image (S/M/L)"}
              </button>
            </div>
          )}

          <div className="relative rounded-xl overflow-hidden bg-gray-100">
            <img src={sizeShown || result || baseImage} alt="" className="w-full object-cover" data-testid="ai-preview-image" />
            {sizeShown && !sizeLoading && (
              <span className="absolute top-2 start-2 text-[10px] font-black px-2 py-1 rounded-full bg-emerald-700 text-white shadow"
                    data-testid="ai-preview-size-badge">
                {sizeBadge}
              </span>
            )}
            {(loading || sizeLoading) && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-white/60 backdrop-blur-sm">
                <Loader2 className="animate-spin text-emerald-700" size={32} />
                {sizeLoading ? (
                  <span className="text-[11px] font-semibold text-emerald-900" data-testid="ai-preview-size-image-loading">
                    {ar
                      ? (stemsPick ? `جارٍ توليد صورة ${stemsPick} وردة...` : `جارٍ توليد صورة الحجم ${SIZE_LBL[selectedSize]}...`)
                      : (stemsPick ? `Generating ${stemsPick} stems photo...` : `Generating ${SIZE_LBL[selectedSize]} size photo...`)}
                  </span>
                ) : (
                  <span className="text-[11px] font-semibold text-emerald-900" data-testid="ai-preview-generating">
                    {ar ? "جارٍ التوليد بالذكاء... يستغرق عادة ١٠-٢٠ ثانية" : "Generating with AI... usually takes 10-20s"}
                  </span>
                )}
              </div>
            )}
          </div>

          {result && !loading && resultLabel && product && (
            <button onClick={addCustomToCart} disabled={adding}
                    className="btn-primary w-full !py-2.5 text-sm"
                    data-testid="ai-preview-add-to-cart">
              {adding ? <Loader2 size={15} className="animate-spin" />
                : addedOk ? <Check size={15} /> : <ShoppingCart size={15} />}
              {addedOk
                ? (ar ? "تمت الإضافة — سيُجهّز بهذا الشكل ✓" : "Added — will be prepared like this ✓")
                : (ar
                    ? `أضف للسلة بهذا الشكل (${resultLabel})${addonFee > 0 ? ` — +${addonFee} ر.ق` : ""}`
                    : `Add to cart like this (${resultLabel})${addonFee > 0 ? ` — +${addonFee} QAR` : ""}`)}
            </button>
          )}

          {result && !loading && (
            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={() => { setResult(null); setActive(""); setResultLabel(""); setAddedOk(false); }} className="chip !text-xs" data-testid="ai-preview-reset">
                <RotateCcw size={13} /> {ar ? "الأصلي" : "Original"}
              </button>
              {resultLabel && (
                <button onClick={shareDesign} disabled={sharing}
                        className="chip !text-xs !text-emerald-800 !border-emerald-300"
                        data-testid="ai-preview-share-design">
                  {sharing ? <Loader2 size={13} className="animate-spin" /> : <Share2 size={13} />}
                  {ar ? "شارك تصميمك" : "Share design"}
                </button>
              )}
              {resultAddon !== "size_compare" && (
                <span className="text-[10px] opacity-50">{ar ? "التعديلات تتراكم — «الأصلي» للبدء من جديد" : "Edits stack — tap Original to restart"}</span>
              )}
            </div>
          )}
          {err && <div className="text-xs text-red-600" data-testid="ai-preview-error">{err}</div>}
        </div>
      )}
    </div>
  );
}
